import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import {
  ErrorText,
  FieldLabel,
  FieldLabelContainer,
  FieldWrapper,
  MultiSelectCommaValue,
  MultiSelectFormControl,
  MultiSelectInlinePlaceholder,
} from "styles/formStyleComponents";
import InfoIconWithTooltip from "./InfoIconWithTooltip";

const getOptionValue = (option) => {
  if (option == null) {
    return "";
  }
  if (typeof option === "object" && option.value !== undefined) {
    return option.value;
  }
  return option;
};

const resolveSelectedLabel = (val, options) => {
  if (val == null || val === "") {
    return "";
  }
  if (typeof val === "object") {
    return String(val.label ?? val.value ?? "");
  }
  const match = options.find(
    (opt) =>
      opt === val ||
      String(getOptionValue(opt)) === String(val) ||
      String(opt?.label) === String(val)
  );
  return String(match?.label ?? match?.value ?? val);
};

const normalizeSelectedValues = (selected, options) => {
  const list = Array.isArray(selected)
    ? selected
    : selected != null
      ? [selected]
      : [];
  return list
    .map((item) => {
      if (item == null || item === "") {
        return null;
      }
      if (typeof item === "object") {
        if (item.value !== undefined) {
          return item.value;
        }
        if (item.label !== undefined) {
          const byLabel = options.find(
            (opt) => String(opt?.label) === String(item.label)
          );
          return byLabel != null ? getOptionValue(byLabel) : item.label;
        }
      }
      const match = options.find(
        (opt) =>
          getOptionValue(opt) === item ||
          String(getOptionValue(opt)) === String(item) ||
          String(opt?.label) === String(item)
      );
      return match != null ? getOptionValue(match) : item;
    })
    .filter((value) => value != null && value !== "");
};

const formatCommaSeparatedSelection = (selected, options) =>
  (selected ?? [])
    .map((val) => resolveSelectedLabel(val, options))
    .filter(Boolean)
    .join(", ");

const MultiSelectFieldComponent = ({
  name,
  label,
  value,
  required,
  disabled,
  error,
  info,
  options = [],
  onChange,
  compact = false,
  inline = false,
  valueDisplay = "chips",
}) => {
  const fieldLabel = required ? `${label} *` : label;
  const hideFloatingLabel = compact && inline;
  const useCommaDisplay = valueDisplay === "comma";
  const selectedValues = normalizeSelectedValues(value, options);
  const hasSelection = selectedValues.length > 0;
  const useFloatingLabel = !hideFloatingLabel && hasSelection;
  const showInlinePlaceholder = hideFloatingLabel || !hasSelection;
  const labelId = `${name}-select-label`;

  const emptyPlaceholder = (
    <MultiSelectInlinePlaceholder hasError={!!error}>
      {fieldLabel}
    </MultiSelectInlinePlaceholder>
  );

  return (
    <FieldWrapper compact={compact} inline={inline}>
      {info && (
        <FieldLabelContainer>
          <FieldLabel>{fieldLabel}</FieldLabel>
          <InfoIconWithTooltip info={info} />
        </FieldLabelContainer>
      )}
      <MultiSelectFormControl
        fullWidth
        size="small"
        error={!!error}
        compactInline={hideFloatingLabel}
        hideLegend={!hideFloatingLabel && !hasSelection}
      >
        {useFloatingLabel ? (
          <InputLabel id={labelId} shrink>
            {fieldLabel}
          </InputLabel>
        ) : null}
        <Select
          multiple
          displayEmpty
          error={!!error}
          labelId={useFloatingLabel ? labelId : undefined}
          label={useFloatingLabel ? fieldLabel : undefined}
          value={selectedValues}
          onChange={(e) => onChange(name, e.target.value)}
          disabled={disabled}
          input={
            <OutlinedInput
              error={!!error}
              notched={useFloatingLabel}
              label={useFloatingLabel ? fieldLabel : undefined}
              inputProps={{
                autoComplete: "off",
                "aria-label": fieldLabel,
              }}
            />
          }
          renderValue={(selected) => {
            if (!selected?.length) {
              if (showInlinePlaceholder) {
                return emptyPlaceholder;
              }
              return null;
            }
            if (useCommaDisplay) {
              return (
                <MultiSelectCommaValue component="span">
                  {formatCommaSeparatedSelection(selected, options)}
                </MultiSelectCommaValue>
              );
            }
            return formatCommaSeparatedSelection(selected, options);
          }}
        >
          {options.map((option) => {
            const optionValue = getOptionValue(option);
            return (
              <MenuItem key={String(optionValue)} value={optionValue}>
                {option?.label || option}
              </MenuItem>
            );
          })}
        </Select>
        {error ? <ErrorText error>{error}</ErrorText> : null}
      </MultiSelectFormControl>
    </FieldWrapper>
  );
};

export default MultiSelectFieldComponent;

